import React from 'react';
import Tooltip from '@tencent/dui/lib/components/Tooltip';
import Dropdown from '@tencent/dui/lib/components/Dropdown';
import ColorPicker from '@tencent/dui/lib/components/ColorPicker';
import styles from './color.less';
import ColorPickerContainer from '@src/js/common/ark-extensions/dc-components/color-picker/color-picker-container';
import { ColorPickerType, ColorSourceType } from '@src/js/common/ark-extensions/dc-components/color-picker/type';
import { getRgbaColorWithAlpha } from '@framework/workbench/drawer/utils/color-options';
import {
    IColorPickerManager,
} from '@src/slide/application/service/color-picker-manager/color-picker-manager.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import { getColorNoAlpha } from '@src/slide/infrastructure/module/independent/color/transform';
import { ITDColor, TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';

interface IColorSelectButtonProps {
    tooltipText?: string;
    defaultValue?: string;
    value?: ITDColor;
    paletteButtonsStyle?: React.CSSProperties,
    colorBoxStyle?: React.CSSProperties,
    iconWrapperStyle?: React.CSSProperties,
    iconStyle?: React.CSSProperties,
    disabledThemePanel?: boolean;
    isStandardColorPicker?: boolean;
    onColorChange?(color: ITDColor, from?: any): void;
    // todo: 埋点标志不一致
    onColorHover?(color: string, from?): void;
    onMouseLeave?(): void;
}

interface IColorPickerPanelState {
    showColorPicker: boolean,
}

export class ColorSelectButton extends React.Component<IColorSelectButtonProps, IColorPickerPanelState> {
    private defaultValue = this.props.defaultValue ?? '#FF0000';

    constructor(props: IColorSelectButtonProps) {
        super(props);
        this.state = {
            showColorPicker: false,
        };
    }

    render() {
        const {
            tooltipText,
            value,
            onColorChange,
            onColorHover,
            onMouseLeave,
            isStandardColorPicker,
        } = this.props;
        const useNewEyeDropper = this.getColorPickerManager()?.useNewEyeDropper;
        // 更多色盘用带alpha的色值
        const colorWithAlpha = value?.rgba || '';
        // 主题色盘和按钮用没alpha的色值
        const colorNoAlpha = value?.srgb_color ? getColorNoAlpha(value?.srgb_color) : '';
        const colorPickerService = this.getColorPickerManager();
        return (<Tooltip title={tooltipText} disabled={!tooltipText} className={styles.tooltip} hideOnClick={true}>
            <div className={styles['palette-buttons']} style={{
                ...this.props.paletteButtonsStyle,
            }}>
                <Dropdown
                    zIndex={10000}
                    dropContent={
                        <ColorPickerContainer
                            onColorHover={onColorHover}
                            onMouseLeave={onMouseLeave}
                            selectedTdColor={value}
                            isStandardColorPicker={isStandardColorPicker}
                            colorPickerType={ColorPickerType.StandardPC}
                            onColorChange={(tdColor, from) => {
                                onColorChange?.(tdColor, from);
                            }}
                        />
                    }
                    visible={this.state.showColorPicker}
                    onVisibleChange={this.changeDropdownVisible}
                >
                    <div
                        className={styles['palette-color-box']}
                        style={{
                            backgroundColor: colorNoAlpha,
                            ...this.props.colorBoxStyle,
                        }}>
                    </div>
                </Dropdown>
                <Dropdown
                    zIndex={10000}
                    dropContent={
                        <Dropdown.Keep>
                            <ColorPicker.Palette
                                eyeDropperDisabled={!useNewEyeDropper}
                                selectedColor={colorWithAlpha}
                                onColorSelect={(color: string) => {
                                    onColorChange?.(new TDColor({ rgbaStr: color }), ColorSourceType.Custom);
                                    colorPickerService?.setRecentColor(color);
                                }}
                                style={{
                                    width: 234,
                                    boxSizing: 'content-box',
                                    backgroundColor: 'rgb(255, 255, 255)',
                                    padding: '12px 10px',
                                    border: '1px solid rgba(0, 0, 0, 0.04)',
                                    boxShadow: 'rgb(0 0 0 / 12%) 0px 2px 6px 0px',
                                    borderRadius: '1px',
                                }}
                            />
                        </Dropdown.Keep>}
                >
                    <div className={styles['palette-icon-container']}>
                        <div className={styles['palette-icon']} style={{
                            ...this.props.iconWrapperStyle,
                        }}>
                            <svg style={{
                                ...this.props.iconStyle,
                            }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16">
                                <g fill="none">
                                    <path d="M0 0h16v16H0z"/>
                                    <path d="M11.6 8c-.533 0-1-.467-1-1 0-.533.467-1 1-1 .533 0 1 .467 1 1 0 .532-.467 1-1 1ZM9.8 5.4c-.533 0-1-.467-1-1 0-.533.467-1 1-1 .533 0 1 .467 1 1 0 .533-.467 1-1 1ZM6.4 5.4c-.534 0-1-.467-1-1 0-.533.466-1 1-1 .533 0 1 .467 1 1 0 .533-.467 1-1 1ZM4.4 8c-.534 0-1-.467-1-1 0-.533.466-1 1-1 .533 0 1 .467 1 1 0 .532-.467 1-1 1Z" fill="currentColor"/>
                                    <path d="M8 1.516h-.002A6.456 6.456 0 0 0 1.516 8 6.456 6.456 0 0 0 8 14.484c.345 0 .664-.32.664-.664 0-.199-.038-.298-.223-.496-.285-.422-.398-.666-.398-1.056 0-.897.768-1.663 1.664-1.663h1.397a3.343 3.343 0 0 0 3.38-3.381c0-3.138-2.88-5.708-6.484-5.708Zm.001-1h.001H8h.001Z" stroke="currentColor"/>
                                </g>
                            </svg>
                        </div>
                    </div>
                </Dropdown>
            </div>
        </Tooltip>);
    }

    private changeDropdownVisible = (showColorPicker: boolean) => {
        this.setState({
            showColorPicker,
        });
    };

    private getColorPickerManager(): IColorPickerManager | undefined {
        return (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
            .editableServiceLayer?.colorPickerManager;
    }
}
